/**
 * Peptide-specific data for calculator pages
 * Each peptide has its own configuration used by PeptideCalculatorTemplate
 */

export const PEPTIDE_DATA = {
  retatrutide: {
    // SEO and page info
    seo: {
      title: 'Retatrutide Calculator - LY3437943 Dosage & Reconstitution Calculator | PeptideDose',
      description: 'Calculate Retatrutide (LY3437943) dosage and reconstitution. Free calculator for the triple agonist weight loss peptide. Supports 1mg to 12mg weekly doses.',
    },
    // Peptide info for structured data
    peptideInfo: {
      name: 'Retatrutide',
      brands: 'LY3437943 (Eli Lilly)',
      summary: 'Retatrutide is a triple agonist peptide targeting GLP-1, GIP, and glucagon receptors, showing unprecedented weight loss of up to 24% in clinical trials.',
      mechanism: 'Triple receptor agonist (GLP-1/GIP/Glucagon) with balanced activity for maximum metabolic benefit.',
      dosing: 'Weekly injection, starting at 1mg and titrating up to 12mg based on tolerance and response.',
      efficacy: 'Phase 2 trials showed up to 24% weight loss in 48 weeks, surpassing all other GLP-1 medications.',
      safety: 'Similar side effects to GLP-1 class including nausea and GI effects, with potential for increased heart rate.',
      status: 'Currently in Phase 3 trials (TRIUMPH program), expected FDA approval 2026.',
    },
    // Hero section
    hero: {
      title: 'Retatrutide Calculator',
      code: 'LY3437943',
      subtitle: 'Triple Agonist Peptide Calculator - GLP-1/GIP/Glucagon',
      badge: 'Phase 3 Clinical Trials - Up to 24% Weight Loss',
    },
    // Quick dose buttons
    quickDoses: {
      title: 'Common Retatrutide Doses',
      defaultVialMg: 10,
      defaultBacWaterMl: 1,
      doses: [
        { label: '1mg/week (Starting)', value: 1 },
        { label: '4mg/week', value: 4 },
        { label: '8mg/week', value: 8 },
        { label: '12mg/week (Maximum)', value: 12 },
      ],
    },
    // Info cards
    infoSection: {
      title: 'About Retatrutide (LY3437943)',
      cards: [
        {
          icon: '🧬',
          title: 'Mechanism of Action',
          intro: 'Retatrutide is a triple receptor agonist that simultaneously activates:',
          items: [
            { bold: 'GLP-1 Receptor:', text: 'Reduces appetite and slows gastric emptying' },
            { bold: 'GIP Receptor:', text: 'Enhances insulin secretion and fat metabolism' },
            { bold: 'Glucagon Receptor:', text: 'Increases energy expenditure' },
          ],
        },
        {
          icon: '📊',
          title: 'Clinical Trial Results',
          intro: 'Phase 2 trial outcomes at 48 weeks:',
          items: [
            { bold: null, text: 'Average weight loss: **24.2%** at 12mg dose' },
            { bold: null, text: '17.5% weight loss at 8mg dose' },
            { bold: null, text: 'Superior to semaglutide and tirzepatide' },
            { bold: null, text: 'Improved metabolic markers' },
          ],
        },
        {
          icon: '💉',
          title: 'Dosing Protocol',
          intro: 'Typical Retatrutide dosing schedule:',
          items: [
            { bold: 'Week 1-4:', text: '1mg weekly' },
            { bold: 'Week 5-8:', text: '4mg weekly' },
            { bold: 'Week 9-12:', text: '8mg weekly' },
            { bold: 'Week 13+:', text: '12mg weekly (if tolerated)' },
          ],
        },
        {
          icon: '⚠️',
          title: 'Important Information',
          intro: null,
          items: [
            { bold: null, text: 'Currently in Phase 3 trials (TRIUMPH program)' },
            { bold: null, text: 'Expected FDA approval: 2026' },
            { bold: null, text: 'Not yet available for prescription' },
            { bold: null, text: 'Research use only at this time' },
            { bold: null, text: 'Consult healthcare provider before use' },
          ],
        },
      ],
    },
    // Guide CTA
    guideCta: {
      title: 'Deep Dive: Complete Retatrutide Guide',
      description: "Learn everything about Retatrutide's triple agonist mechanism, clinical trial results, and how it compares to other GLP-1 medications.",
      linkText: 'Read the Complete Guide →',
      linkPath: '/guides/retatrutide-complete-guide',
    },
    // FAQ items
    faqItems: [
      {
        question: 'What makes Retatrutide different from Semaglutide?',
        answer: 'Retatrutide is a triple agonist (GLP-1/GIP/Glucagon) while Semaglutide only targets GLP-1. This triple action leads to greater weight loss (24% vs 15%) and increased energy expenditure.',
      },
      {
        question: 'How do I reconstitute Retatrutide?',
        answer: 'Add bacteriostatic water slowly to the vial, aiming the stream against the glass wall. For a 10mg vial, adding 1mL creates a 10mg/mL concentration. Use our calculator above for precise measurements.',
      },
      {
        question: 'What syringe should I use for Retatrutide?',
        answer: 'Most users prefer 1mL (100 unit) insulin syringes for accuracy. For doses above 1mL, consider using a 3mL syringe.',
      },
      {
        question: 'When will Retatrutide be FDA approved?',
        answer: 'Phase 3 trials are ongoing with the TRIUMPH program. FDA approval is anticipated in 2026 based on current trial timelines.',
      },
    ],
  },

  tirzepatide: {
    seo: {
      title: 'Tirzepatide Calculator - Mounjaro & Zepbound Dosage Calculator | PeptideDose',
      description: 'Calculate Tirzepatide (Mounjaro/Zepbound) dosage and reconstitution. Free calculator for the dual agonist GLP-1/GIP weight loss medication. Supports 2.5mg to 15mg weekly doses.',
    },
    peptideInfo: {
      name: 'Tirzepatide',
      brands: 'Mounjaro, Zepbound (Eli Lilly)',
      summary: 'Tirzepatide is a dual GLP-1/GIP receptor agonist showing superior weight loss of up to 22.5% in clinical trials.',
      mechanism: 'Dual receptor agonist (GLP-1/GIP) with balanced activity for enhanced metabolic benefits.',
      dosing: 'Weekly injection, starting at 2.5mg and titrating up to 15mg based on tolerance and response.',
      efficacy: 'SURMOUNT trials showed up to 22.5% weight loss at 15mg dose over 72 weeks.',
      safety: 'Similar side effects to GLP-1 class including nausea and GI effects, generally well-tolerated.',
      status: 'FDA approved for diabetes (Mounjaro) and obesity (Zepbound).',
    },
    hero: {
      title: 'Tirzepatide Calculator',
      code: 'Mounjaro / Zepbound',
      subtitle: 'Dual Agonist Peptide Calculator - GLP-1/GIP',
      badge: 'FDA Approved - Up to 22.5% Weight Loss',
    },
    quickDoses: {
      title: 'Common Tirzepatide Doses',
      defaultVialMg: 10,
      defaultBacWaterMl: 1,
      doses: [
        { label: '2.5mg/week (Starting)', value: 2.5 },
        { label: '5mg/week', value: 5 },
        { label: '10mg/week', value: 10 },
        { label: '15mg/week (Maximum)', value: 15 },
      ],
    },
    infoSection: {
      title: 'About Tirzepatide (Mounjaro/Zepbound)',
      cards: [
        {
          icon: '🧬',
          title: 'Mechanism of Action',
          intro: 'Tirzepatide is a dual receptor agonist that activates:',
          items: [
            { bold: 'GLP-1 Receptor:', text: 'Reduces appetite and slows gastric emptying' },
            { bold: 'GIP Receptor:', text: 'Enhances insulin secretion and fat metabolism' },
            { bold: null, text: 'Provides superior weight loss compared to GLP-1 only medications' },
          ],
        },
        {
          icon: '📊',
          title: 'Clinical Trial Results',
          intro: 'SURMOUNT trial outcomes at 72 weeks:',
          items: [
            { bold: null, text: 'Average weight loss: **22.5%** at 15mg dose' },
            { bold: null, text: '20.9% weight loss at 10mg dose' },
            { bold: null, text: '19.5% weight loss at 5mg dose' },
            { bold: null, text: 'Superior to Semaglutide in head-to-head trials' },
          ],
        },
        {
          icon: '💉',
          title: 'Dosing Protocol',
          intro: 'Typical Tirzepatide dosing schedule:',
          items: [
            { bold: 'Week 1-4:', text: '2.5mg weekly' },
            { bold: 'Week 5-8:', text: '5mg weekly' },
            { bold: 'Week 9-12:', text: '7.5mg weekly' },
            { bold: 'Week 13-16:', text: '10mg weekly' },
            { bold: 'Week 17-20:', text: '12.5mg weekly' },
            { bold: 'Week 21+:', text: '15mg weekly (maximum)' },
          ],
        },
        {
          icon: '⚠️',
          title: 'Important Information',
          intro: null,
          items: [
            { bold: null, text: 'FDA approved as Mounjaro for diabetes' },
            { bold: null, text: 'FDA approved as Zepbound for weight loss' },
            { bold: null, text: 'Requires prescription from healthcare provider' },
            { bold: null, text: 'Weekly subcutaneous injection' },
            { bold: null, text: 'Store in refrigerator after reconstitution' },
          ],
        },
      ],
    },
    guideCta: {
      title: 'Deep Dive: Tirzepatide vs Semaglutide',
      description: 'Learn how Tirzepatide compares to Semaglutide and other GLP-1 medications in our comprehensive comparison guide.',
      linkText: 'Read the Comparison Guide →',
      linkPath: '/guides/tirzepatide-vs-semaglutide',
    },
    faqItems: [
      {
        question: "What's the difference between Mounjaro and Zepbound?",
        answer: 'Both contain the same active ingredient (Tirzepatide). Mounjaro is FDA approved for type 2 diabetes, while Zepbound is approved specifically for weight loss. The medication is identical.',
      },
      {
        question: 'How does Tirzepatide compare to Ozempic/Wegovy?',
        answer: 'Tirzepatide is a dual agonist (GLP-1/GIP) while Ozempic/Wegovy (Semaglutide) only targets GLP-1. Tirzepatide shows superior weight loss (22.5% vs 15-17%) in clinical trials.',
      },
      {
        question: 'How do I reconstitute Tirzepatide?',
        answer: 'Add bacteriostatic water slowly to the vial, aiming the stream against the glass wall. For a 10mg vial, adding 1mL creates a 10mg/mL concentration. Use our calculator above for precise measurements.',
      },
      {
        question: 'What are common side effects of Tirzepatide?',
        answer: 'Most common side effects include nausea, diarrhea, decreased appetite, and vomiting. These typically improve over time and with gradual dose escalation.',
      },
    ],
  },

  semaglutide: {
    seo: {
      title: 'Semaglutide Calculator - Ozempic & Wegovy Dosage Calculator | PeptideDose',
      description: 'Calculate Semaglutide (Ozempic/Wegovy) dosage and reconstitution. Free calculator for the GLP-1 weight loss medication. Supports 0.25mg to 2.4mg weekly doses.',
    },
    peptideInfo: {
      name: 'Semaglutide',
      brands: 'Ozempic, Wegovy, Rybelsus (Novo Nordisk)',
      summary: 'Semaglutide is a GLP-1 receptor agonist showing 15-17% weight loss in clinical trials.',
      mechanism: 'GLP-1 receptor agonist that reduces appetite and slows gastric emptying.',
      dosing: 'Weekly injection, starting at 0.25mg and titrating up to 2.4mg based on tolerance.',
      efficacy: 'STEP trials showed 15-17% weight loss at 2.4mg dose over 68 weeks.',
      safety: 'Common side effects include nausea, diarrhea, and vomiting that typically improve over time.',
      status: 'FDA approved for diabetes (Ozempic) and obesity (Wegovy). Oral version available as Rybelsus.',
    },
    hero: {
      title: 'Semaglutide Calculator',
      code: 'Ozempic / Wegovy',
      subtitle: 'GLP-1 Agonist Peptide Calculator',
      badge: 'FDA Approved - Up to 17% Weight Loss',
    },
    quickDoses: {
      title: 'Common Semaglutide Doses',
      defaultVialMg: 5,
      defaultBacWaterMl: 1,
      doses: [
        { label: '0.25mg/week (Starting)', value: 0.25 },
        { label: '0.5mg/week', value: 0.5 },
        { label: '1mg/week', value: 1 },
        { label: '2.4mg/week (Maximum)', value: 2.4 },
      ],
    },
    infoSection: {
      title: 'About Semaglutide (Ozempic/Wegovy)',
      cards: [
        {
          icon: '🧬',
          title: 'Mechanism of Action',
          intro: 'Semaglutide is a GLP-1 receptor agonist that:',
          items: [
            { bold: 'Reduces appetite:', text: 'Acts on brain centers controlling hunger' },
            { bold: 'Slows gastric emptying:', text: 'Keeps you feeling full longer' },
            { bold: 'Improves insulin response:', text: 'Better blood sugar control' },
            { bold: 'Decreases glucagon:', text: 'Reduces glucose production' },
          ],
        },
        {
          icon: '📊',
          title: 'Clinical Trial Results',
          intro: 'STEP trial outcomes at 68 weeks:',
          items: [
            { bold: null, text: 'Average weight loss: **14.9%** at 2.4mg dose' },
            { bold: null, text: '16% weight loss with lifestyle intervention' },
            { bold: null, text: '35% of patients lost >20% body weight' },
            { bold: null, text: 'Significant improvements in cardiovascular markers' },
          ],
        },
        {
          icon: '💉',
          title: 'Dosing Protocol',
          intro: 'Typical Semaglutide dosing schedule:',
          items: [
            { bold: 'Week 1-4:', text: '0.25mg weekly' },
            { bold: 'Week 5-8:', text: '0.5mg weekly' },
            { bold: 'Week 9-12:', text: '1mg weekly' },
            { bold: 'Week 13-16:', text: '1.7mg weekly' },
            { bold: 'Week 17+:', text: '2.4mg weekly (maximum)' },
          ],
        },
        {
          icon: '⚠️',
          title: 'Important Information',
          intro: null,
          items: [
            { bold: null, text: 'FDA approved as Ozempic for diabetes (2mg max)' },
            { bold: null, text: 'FDA approved as Wegovy for weight loss (2.4mg max)' },
            { bold: null, text: 'Oral version available as Rybelsus' },
            { bold: null, text: 'Requires prescription from healthcare provider' },
            { bold: null, text: 'Store in refrigerator after reconstitution' },
          ],
        },
      ],
    },
    guideCta: {
      title: 'Deep Dive: Semaglutide vs Tirzepatide',
      description: 'Learn how Semaglutide compares to newer GLP-1 medications in our comprehensive comparison guide.',
      linkText: 'Read the Comparison Guide →',
      linkPath: '/guides/tirzepatide-vs-semaglutide',
    },
    faqItems: [
      {
        question: "What's the difference between Ozempic and Wegovy?",
        answer: 'Both contain the same active ingredient (Semaglutide). Ozempic is FDA approved for type 2 diabetes with a maximum dose of 2mg, while Wegovy is approved for weight loss with a maximum dose of 2.4mg.',
      },
      {
        question: 'How does Semaglutide compare to Tirzepatide?',
        answer: 'Semaglutide is a single GLP-1 agonist showing 15-17% weight loss, while Tirzepatide is a dual agonist (GLP-1/GIP) showing 22.5% weight loss. Both are effective, but Tirzepatide shows superior results.',
      },
      {
        question: 'How do I reconstitute Semaglutide?',
        answer: 'Add bacteriostatic water slowly to the vial, aiming the stream against the glass wall. For a 5mg vial, adding 1mL creates a 5mg/mL concentration. Use our calculator above for precise measurements.',
      },
      {
        question: 'What are common side effects of Semaglutide?',
        answer: 'Most common side effects include nausea (20-40%), diarrhea, constipation, and vomiting. These typically improve after 4-8 weeks as your body adjusts.',
      },
    ],
  },

  bpc157: {
    seo: {
      title: 'BPC-157 Calculator - Body Protection Compound Dosage Calculator | PeptideDose',
      description: 'Calculate BPC-157 dosage and reconstitution. Free calculator for the healing peptide BPC-157. Supports 250mcg to 1mg daily doses.',
    },
    peptideInfo: {
      name: 'BPC-157',
      brands: 'Body Protection Compound-157',
      summary: 'BPC-157 is a healing peptide showing remarkable tissue repair and anti-inflammatory properties in research studies.',
      mechanism: 'Promotes angiogenesis, tissue healing, and reduces inflammation through multiple growth factor pathways.',
      dosing: 'Typical research doses range from 250-500mcg daily, administered subcutaneously or orally.',
      efficacy: 'Studies show accelerated healing of tendons, ligaments, muscles, and gut tissue with minimal side effects.',
      safety: 'Well-tolerated in research with minimal reported side effects. Some users report mild dizziness or fatigue.',
      status: 'Research compound not approved for human use. Widely studied for healing and regenerative properties.',
    },
    hero: {
      title: 'BPC-157 Calculator',
      code: 'Body Protection Compound',
      subtitle: 'Healing Peptide Dosage Calculator',
      badge: 'Research Peptide - Tissue Repair & Healing',
    },
    quickDoses: {
      title: 'Common BPC-157 Doses',
      defaultVialMg: 5,
      defaultBacWaterMl: 2,
      doses: [
        { label: '250mcg/day (Low)', value: 0.25 },
        { label: '500mcg/day (Standard)', value: 0.5 },
        { label: '750mcg/day (High)', value: 0.75 },
        { label: '1mg/day (Maximum)', value: 1 },
      ],
    },
    infoSection: {
      title: 'About BPC-157 (Body Protection Compound)',
      cards: [
        {
          icon: '🧬',
          title: 'Mechanism of Action',
          intro: 'BPC-157 works through multiple pathways:',
          items: [
            { bold: 'Angiogenesis:', text: 'Promotes new blood vessel formation' },
            { bold: 'Growth factors:', text: 'Upregulates VEGF, FAK, and paxillin' },
            { bold: 'Nitric oxide:', text: 'Modulates NO system for healing' },
            { bold: 'Anti-inflammatory:', text: 'Reduces inflammatory cytokines' },
          ],
        },
        {
          icon: '📊',
          title: 'Research Findings',
          intro: 'Studies have demonstrated:',
          items: [
            { bold: 'Tendon healing:', text: '2-3x faster recovery in animal models' },
            { bold: 'Gut repair:', text: 'Effective for IBD and ulcers' },
            { bold: 'Muscle recovery:', text: 'Reduced healing time by 30-50%' },
            { bold: 'Neuroprotection:', text: 'Shows promise for nerve damage' },
          ],
        },
        {
          icon: '💉',
          title: 'Dosing Protocol',
          intro: 'Common research protocols:',
          items: [
            { bold: 'Standard dose:', text: '250-500mcg daily' },
            { bold: 'Injection frequency:', text: 'Once or twice daily' },
            { bold: 'Cycle length:', text: '4-12 weeks typical' },
            { bold: 'Routes:', text: 'Subcutaneous (most common) or oral' },
            { bold: 'Site:', text: 'Can inject near injury site or abdomen' },
          ],
        },
        {
          icon: '⚠️',
          title: 'Important Information',
          intro: null,
          items: [
            { bold: null, text: 'Research compound only - not FDA approved' },
            { bold: null, text: 'Limited human clinical trials available' },
            { bold: null, text: 'Quality varies between suppliers' },
            { bold: null, text: 'Store reconstituted peptide in refrigerator' },
            { bold: null, text: 'Consult healthcare provider before use' },
          ],
        },
      ],
    },
    guideCta: {
      title: 'Learn More: BPC-157 Complete Guide',
      description: 'Discover the healing potential of BPC-157, research findings, and proper usage protocols.',
      linkText: 'Read Peptide Reconstitution Guide →',
      linkPath: '/guides/peptide-reconstitution',
    },
    faqItems: [
      {
        question: 'What makes BPC-157 different from other healing peptides?',
        answer: "BPC-157 is unique in its systemic healing effects, working throughout the body rather than locally. It's particularly effective for tendon, ligament, muscle, and gut healing with minimal side effects.",
      },
      {
        question: 'Can BPC-157 be taken orally?',
        answer: 'Yes, BPC-157 is stable in gastric acid and can be taken orally, especially for gut-related issues. However, subcutaneous injection is more common for systemic effects and injuries outside the GI tract.',
      },
      {
        question: 'How long does a BPC-157 cycle typically last?',
        answer: 'Most research protocols use 4-12 week cycles. Some users take breaks between cycles, while others use it continuously at lower doses for maintenance.',
      },
      {
        question: 'Should BPC-157 be injected near the injury site?',
        answer: 'While some prefer localized injection near injury sites, BPC-157 has systemic effects and can be injected subcutaneously in the abdomen with similar results.',
      },
    ],
  },

  cjc1295: {
    seo: {
      title: 'CJC-1295 Calculator - Growth Hormone Peptide Dosage Calculator | PeptideDose',
      description: 'Calculate CJC-1295 with DAC and without DAC dosage and reconstitution. Free calculator for the HGH-releasing peptide CJC-1295.',
    },
    peptideInfo: {
      name: 'CJC-1295',
      brands: 'CJC-1295 with DAC, CJC-1295 without DAC (Mod GRF 1-29)',
      summary: 'CJC-1295 is a growth hormone releasing hormone (GHRH) analog that stimulates natural HGH production.',
      mechanism: 'Binds to GHRH receptors on the pituitary gland, triggering sustained release of growth hormone.',
      dosing: 'With DAC: 1-2mg weekly. Without DAC: 100-300mcg 2-3 times daily.',
      efficacy: 'Increases IGF-1 levels by 200-300%, improves body composition, sleep quality, and recovery.',
      safety: 'Common side effects include flushing, tiredness, and water retention. Generally well-tolerated.',
      status: 'Research compound used for anti-aging and performance enhancement. Not FDA approved.',
    },
    hero: {
      title: 'CJC-1295 Calculator',
      code: 'GHRH Analog',
      subtitle: 'Growth Hormone Releasing Peptide Calculator',
      badge: 'HGH Secretagogue - Anti-Aging & Performance',
    },
    quickDoses: {
      title: 'Common CJC-1295 Doses',
      defaultVialMg: 2,
      defaultBacWaterMl: 1,
      doses: [
        { label: '100mcg (No DAC)', value: 0.1 },
        { label: '300mcg (No DAC Max)', value: 0.3 },
        { label: '1mg/week (With DAC)', value: 1 },
        { label: '2mg/week (DAC Max)', value: 2 },
      ],
    },
    infoSection: {
      title: 'About CJC-1295',
      cards: [
        {
          icon: '🧬',
          title: 'Mechanism of Action',
          intro: 'CJC-1295 stimulates natural HGH production:',
          items: [
            { bold: 'GHRH receptor binding:', text: 'Activates pituitary gland' },
            { bold: 'Pulsatile release:', text: 'Mimics natural GH rhythm' },
            { bold: 'IGF-1 elevation:', text: 'Increases by 200-300%' },
            { bold: 'DAC technology:', text: 'Extends half-life to 6-8 days' },
          ],
        },
        {
          icon: '📊',
          title: 'Research Benefits',
          intro: 'Studies and user reports indicate:',
          items: [
            { bold: 'Body composition:', text: 'Reduced fat, increased lean mass' },
            { bold: 'Recovery:', text: 'Faster healing and muscle repair' },
            { bold: 'Sleep quality:', text: 'Deeper, more restorative sleep' },
            { bold: 'Skin & hair:', text: 'Improved texture and thickness' },
            { bold: 'Energy:', text: 'Enhanced vitality and stamina' },
          ],
        },
        {
          icon: '💉',
          title: 'Dosing Protocol',
          intro: 'Two versions with different protocols:',
          items: [
            { bold: 'With DAC:', text: '1-2mg once weekly' },
            { bold: 'Without DAC:', text: '100-300mcg 2-3x daily' },
            { bold: 'Timing:', text: 'Before bed or post-workout' },
            { bold: 'Cycle:', text: '8-12 weeks on, 4 weeks off' },
            { bold: 'Stack:', text: 'Often combined with Ipamorelin' },
          ],
        },
        {
          icon: '⚠️',
          title: 'Important Information',
          intro: null,
          items: [
            { bold: null, text: 'Research compound - not FDA approved' },
            { bold: null, text: 'May cause water retention initially' },
            { bold: null, text: 'Can increase hunger and cortisol' },
            { bold: null, text: 'Avoid if history of cancer' },
            { bold: null, text: 'Store reconstituted in refrigerator' },
          ],
        },
      ],
    },
    guideCta: {
      title: 'Learn More: Growth Hormone Peptides',
      description: 'Understand how CJC-1295 and other growth hormone peptides work for anti-aging and performance.',
      linkText: 'Read Peptide Guide →',
      linkPath: '/guides/peptide-reconstitution',
    },
    faqItems: [
      {
        question: "What's the difference between CJC-1295 with DAC and without DAC?",
        answer: 'CJC-1295 with DAC has a Drug Affinity Complex that extends its half-life to 6-8 days, allowing once-weekly dosing. Without DAC (Mod GRF 1-29) has a 30-minute half-life and requires multiple daily injections but offers more control over GH pulses.',
      },
      {
        question: 'Should I combine CJC-1295 with other peptides?',
        answer: 'CJC-1295 is commonly stacked with GHRP peptides like Ipamorelin or GHRP-6. This combination creates a synergistic effect, significantly increasing GH release compared to either peptide alone.',
      },
      {
        question: 'When is the best time to inject CJC-1295?',
        answer: 'For CJC-1295 without DAC, inject on an empty stomach before bed or post-workout when GH release is naturally higher. With DAC, timing is less critical due to its long half-life.',
      },
      {
        question: 'What side effects should I watch for?',
        answer: 'Common side effects include facial flushing, tiredness, water retention, and increased hunger. Some users experience tingling or numbness in extremities. These typically subside as the body adjusts.',
      },
    ],
  },

  ipamorelin: {
    seo: {
      title: 'Ipamorelin Calculator - Growth Hormone Peptide Dosage Calculator | PeptideDose',
      description: 'Calculate Ipamorelin dosage and reconstitution. Free calculator for the selective HGH-releasing peptide Ipamorelin.',
    },
    peptideInfo: {
      name: 'Ipamorelin',
      brands: 'Ipamorelin Acetate',
      summary: 'Ipamorelin is a selective growth hormone secretagogue that stimulates HGH release without affecting cortisol or prolactin.',
      mechanism: 'Selective GHRP that binds to ghrelin receptors, triggering GH release without unwanted hormone elevation.',
      dosing: 'Typical dose: 200-300mcg 2-3 times daily. Maximum single dose: 500mcg.',
      efficacy: 'Increases GH levels 7-fold, improves sleep, recovery, and body composition with minimal side effects.',
      safety: 'One of the safest GHRPs with no cortisol or prolactin elevation. Minimal hunger increase.',
      status: 'Research compound widely used for anti-aging and performance. Not FDA approved for human use.',
    },
    hero: {
      title: 'Ipamorelin Calculator',
      code: 'Selective GHRP',
      subtitle: 'Growth Hormone Releasing Peptide Calculator',
      badge: 'Safest GHRP - No Cortisol/Prolactin Effects',
    },
    quickDoses: {
      title: 'Common Ipamorelin Doses',
      defaultVialMg: 5,
      defaultBacWaterMl: 2,
      doses: [
        { label: '200mcg (Standard)', value: 0.2 },
        { label: '300mcg (Optimal)', value: 0.3 },
        { label: '400mcg (High)', value: 0.4 },
        { label: '500mcg (Maximum)', value: 0.5 },
      ],
    },
    infoSection: {
      title: 'About Ipamorelin',
      cards: [
        {
          icon: '🧬',
          title: 'Mechanism of Action',
          intro: 'Ipamorelin is highly selective:',
          items: [
            { bold: 'Ghrelin receptor:', text: 'Selective activation' },
            { bold: 'GH pulse:', text: 'Natural, pulsatile release' },
            { bold: 'No cortisol:', text: 'Unlike other GHRPs' },
            { bold: 'No prolactin:', text: 'Avoids unwanted side effects' },
            { bold: 'Minimal hunger:', text: 'Less ghrelin mimicry' },
          ],
        },
        {
          icon: '📊',
          title: 'Research Benefits',
          intro: 'Clinical and anecdotal evidence shows:',
          items: [
            { bold: 'GH increase:', text: '7-fold elevation post-injection' },
            { bold: 'Fat loss:', text: 'Enhanced lipolysis' },
            { bold: 'Muscle gain:', text: 'Improved protein synthesis' },
            { bold: 'Sleep:', text: 'Deeper, more restorative sleep' },
            { bold: 'Recovery:', text: 'Faster healing and repair' },
            { bold: 'Bone density:', text: 'Increased mineralization' },
          ],
        },
        {
          icon: '💉',
          title: 'Dosing Protocol',
          intro: 'Optimal Ipamorelin protocols:',
          items: [
            { bold: 'Standard dose:', text: '200-300mcg per injection' },
            { bold: 'Frequency:', text: '2-3 times daily' },
            { bold: 'Timing:', text: 'Morning, post-workout, bedtime' },
            { bold: 'Empty stomach:', text: '2 hours after meals' },
            { bold: 'Stack:', text: 'Excellent with CJC-1295' },
            { bold: 'Cycle:', text: 'Can be used continuously' },
          ],
        },
        {
          icon: '⚠️',
          title: 'Important Information',
          intro: null,
          items: [
            { bold: null, text: 'Research compound - not FDA approved' },
            { bold: null, text: 'One of the safest GH peptides available' },
            { bold: null, text: 'No significant side effects at normal doses' },
            { bold: null, text: 'May cause mild headaches initially' },
            { bold: null, text: 'Store reconstituted in refrigerator' },
            { bold: null, text: 'Quality varies between suppliers' },
          ],
        },
      ],
    },
    guideCta: {
      title: 'Learn More: Growth Hormone Optimization',
      description: 'Discover how Ipamorelin and other GH peptides can enhance recovery, sleep, and body composition.',
      linkText: 'Read Peptide Guide →',
      linkPath: '/guides/peptide-reconstitution',
    },
    faqItems: [
      {
        question: 'Why is Ipamorelin considered the safest GHRP?',
        answer: 'Ipamorelin is selective for GH release only, without affecting cortisol, prolactin, or ACTH levels. This selectivity means fewer side effects and no stress hormone elevation, making it ideal for long-term use.',
      },
      {
        question: 'Can I stack Ipamorelin with CJC-1295?',
        answer: 'Yes, this is one of the most popular and effective stacks. CJC-1295 amplifies your natural GH pulses, while Ipamorelin creates additional pulses. Together they create a synergistic effect for maximum GH release.',
      },
      {
        question: 'How quickly will I see results from Ipamorelin?',
        answer: 'Sleep quality often improves within days. Recovery and energy improvements are noticed within 2-3 weeks. Body composition changes typically become visible after 6-8 weeks of consistent use.',
      },
      {
        question: 'Should I cycle Ipamorelin or can I use it continuously?',
        answer: 'Unlike many peptides, Ipamorelin can be used continuously without developing tolerance. Many users run it year-round, though some prefer to cycle 5 days on, 2 days off to maintain receptor sensitivity.',
      },
    ],
  },
};
